import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Send,
  Refresh,
  People,
  Person,
  Email,
  NotificationsActive,
  Search,
  Campaign,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { usePermissions } from '../../context/PermissionContext';
import { AdminGuard } from '../../components/PermissionGuard';
import API_BASE_URL from '../../config/api';

const NotificationManagement = () => {
  const theme = useTheme();
  const { isAdmin, isOwner } = usePermissions();

  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [sendToAll, setSendToAll] = useState(true);
  const [deliveryMethod, setDeliveryMethod] = useState('notification'); // 'notification', 'email', 'both'
  const [emailSubject, setEmailSubject] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Fetch users for selection
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.users || []);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      (user.emailID || '').toLowerCase().includes(searchLower)
    );
  });

  // Handle user selection
  const handleSelectUser = (userNo) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userNo)) {
        return prev.filter(id => id !== userNo);
      } else {
        return [...prev, userNo];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map(u => u.userNo));
    }
  };

  // Handle send button click — ask for confirmation when emailing all users
  const handleSendClick = () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (!sendToAll && selectedUserIds.length === 0) {
      setError('Please select at least one user or enable "Send to All"');
      return;
    }

    const willSendEmail = deliveryMethod === 'email' || deliveryMethod === 'both';
    if (sendToAll && willSendEmail) {
      setConfirmOpen(true);
      return;
    }

    handleSendNotification();
  };

  // Handle send notification
  const handleSendNotification = async () => {
    setConfirmOpen(false);
    setSending(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/Notification/admin/broadcast`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message.trim(),
          isError,
          sendToAll,
          sendNotification: deliveryMethod === 'notification' || deliveryMethod === 'both',
          sendEmail: deliveryMethod === 'email' || deliveryMethod === 'both',
          emailSubject: emailSubject.trim() || 'Theezdocs Notification',
          userIds: sendToAll ? null : selectedUserIds,
        }),
      });

      const data = await response.json();

      if (data.success) {
        let msg = `Successfully sent notification to ${data.successCount} user(s)${data.failCount > 0 ? ` (${data.failCount} failed)` : ''}`;
        if (data.emailsSent > 0) msg += `. ${data.emailsSent} email(s) sent.`;
        if (data.emailsFailed > 0) msg += ` ${data.emailsFailed} email(s) failed.`;
        setSuccess(msg);
        setMessage('');
        setEmailSubject('');
        setSelectedUserIds([]);
        setSendToAll(true);
        setDeliveryMethod('notification');
      } else {
        setError(data.message || 'Failed to send notification');
      }
    } catch (err) {
      console.error('Error sending notification:', err);
      setError('Failed to send notification. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <AdminGuard>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, mb: 1 }}>
          <Campaign sx={{ fontSize: { xs: 30, md: 40 }, color: theme.palette.primary.main }} />
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            Send Notifications
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Broadcast notifications to all users or select specific recipients
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Grid container spacing={3}>
          {/* Message Composition */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Compose Message
              </Typography>

              <TextField
                label="Notification Message"
                multiline
                rows={4}
                fullWidth
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your notification message here..."
                sx={{ mb: 3 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={isError}
                    onChange={(e) => setIsError(e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isError ? <ErrorIcon color="error" /> : <CheckCircle color="success" />}
                    <Typography>
                      {isError ? 'Error/Warning Notification' : 'Normal Notification'}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={sendToAll}
                    onChange={(e) => setSendToAll(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {sendToAll ? <People color="primary" /> : <Person color="action" />}
                    <Typography>
                      {sendToAll ? 'Send to All Users' : 'Send to Selected Users'}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Delivery Method</Typography>
              <ToggleButtonGroup
                value={deliveryMethod}
                exclusive
                onChange={(e, val) => { if (val) setDeliveryMethod(val); }}
                size="small"
                fullWidth
                sx={{ mb: 2, '& .MuiToggleButton-root': { textTransform: 'none', px: { xs: 1, md: 2 }, fontSize: { xs: 12, md: 14 } } }}
              >
                <ToggleButton value="notification">
                  <NotificationsActive sx={{ fontSize: 16, mr: 0.5 }} /> Notification
                </ToggleButton>
                <ToggleButton value="email">
                  <Email sx={{ fontSize: 16, mr: 0.5 }} /> Email
                </ToggleButton>
                <ToggleButton value="both">
                  <NotificationsActive sx={{ fontSize: 16, mr: 0.5 }} />+<Email sx={{ fontSize: 16, ml: 0.5, mr: 0.5 }} /> Both
                </ToggleButton>
              </ToggleButtonGroup>

              {(deliveryMethod === 'email' || deliveryMethod === 'both') && (
                <TextField
                  label="Email Subject"
                  fullWidth
                  size="small"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Theezdocs Notification"
                  sx={{ mb: 2 }}
                />
              )}

              {/* Summary Card */}
              <Card variant="outlined" sx={{ mb: 3, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notification Preview
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      size="small"
                      label={isError ? 'Warning' : 'Info'}
                      color={isError ? 'error' : 'primary'}
                      icon={isError ? <ErrorIcon /> : <CheckCircle />}
                    />
                    <Chip
                      size="small"
                      label={sendToAll ? `All Users (${users.length})` : `${selectedUserIds.length} Selected`}
                      variant="outlined"
                      icon={sendToAll ? <People /> : <Person />}
                    />
                    <Chip
                      size="small"
                      label={deliveryMethod === 'notification' ? 'Notification Only' : deliveryMethod === 'email' ? 'Email Only' : 'Notification + Email'}
                      color={deliveryMethod === 'both' ? 'secondary' : 'default'}
                      variant={deliveryMethod === 'notification' ? 'outlined' : 'filled'}
                      icon={deliveryMethod === 'email' ? <Email /> : deliveryMethod === 'both' ? <Send /> : <NotificationsActive />}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: message ? 'normal' : 'italic', color: message ? 'text.primary' : 'text.secondary' }}>
                    {message || 'Enter a message above...'}
                  </Typography>
                </CardContent>
              </Card>

              <Button
                variant="contained"
                color={isError ? 'error' : 'primary'}
                size="large"
                fullWidth
                startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
                onClick={handleSendClick}
                disabled={sending || !message.trim() || (!sendToAll && selectedUserIds.length === 0)}
              >
                {sending ? 'Sending...' : 'Send Notification'}
              </Button>
            </Paper>
          </Grid>

          {/* User Selection */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, md: 3 },
                height: '100%',
                opacity: sendToAll ? 0.6 : 1,
                pointerEvents: sendToAll ? 'none' : 'auto',
                transition: 'opacity 0.3s',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Select Recipients
                </Typography>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={fetchUsers}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>

              {sendToAll && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Disable "Send to All" to select specific recipients
                </Alert>
              )}

              <TextField
                placeholder="Search users..."
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  label={`${selectedUserIds.length} selected`}
                  color="primary"
                  variant={selectedUserIds.length > 0 ? 'filled' : 'outlined'}
                />
                <Button size="small" onClick={handleSelectAll}>
                  {selectedUserIds.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              <TableContainer sx={{ maxHeight: 350, overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={{ minWidth: 480 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < filteredUsers.length}
                          checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Role</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={30} />
                        </TableCell>
                      </TableRow>
                    ) : filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => (
                        <TableRow
                          key={user.userNo}
                          hover
                          onClick={() => handleSelectUser(user.userNo)}
                          sx={{ cursor: 'pointer' }}
                          selected={selectedUserIds.includes(user.userNo)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={selectedUserIds.includes(user.userNo)} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {user.firstName} {user.lastName}
                          </TableCell>
                          <TableCell sx={{ maxWidth: { xs: 140, md: 220 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user.emailID}
                          </TableCell>
                          <TableCell>
                            {user.isOwner && (
                              <Chip label="Owner" size="small" color="warning" sx={{ mr: 0.5 }} />
                            )}
                            {user.isAdmin && !user.isOwner && (
                              <Chip label="Admin" size="small" color="info" sx={{ mr: 0.5 }} />
                            )}
                            {!user.isAdmin && !user.isOwner && (
                              <Chip label="User" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirm-send-all-title"
      >
        <DialogTitle id="confirm-send-all-title">Send email to all users?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to send an email to <strong>all {users.length} user(s)</strong>. This action cannot be undone. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendNotification}
            color="primary"
            variant="contained"
            disabled={sending}
            startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
          >
            {sending ? 'Sending...' : 'Yes, send to all'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminGuard>
  );
};

export default NotificationManagement;
